import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { arrayOfAll } from '@bemlo/utils'

import {
  CSSProperties,
  SpacingValue,
  StyleProps,
  createStyles,
} from '../utils/create-styles'

const FLEX_VALUE_MAP = {
  around: 'space-around',
  auto: 'auto',
  baseline: 'baseline',
  between: 'space-between',
  center: 'center',
  end: 'flex-end',
  evenly: 'space-evenly',
  normal: 'normal',
  start: 'flex-start',
  stretch: 'stretch',
} as const

type FlexShorthands = {
  col?: boolean
  columnGap?: SpacingValue
  content?:
    | 'normal'
    | 'center'
    | 'start'
    | 'end'
    | 'between'
    | 'around'
    | 'evenly'
    | 'baseline'
    | 'stretch'
  gap?: SpacingValue
  grow?: CSSProperties['flexGrow']
  items?: 'start' | 'end' | 'center' | 'baseline' | 'stretch'
  justify?:
    | 'normal'
    | 'start'
    | 'end'
    | 'center'
    | 'between'
    | 'around'
    | 'evenly'
    | 'stretch'
  justifyItems?: 'start' | 'end' | 'center' | 'stretch'
  justifySelf?: 'auto' | 'start' | 'end' | 'center' | 'stretch'
  reverse?: boolean
  rowGap?: SpacingValue
  self?: 'auto' | 'start' | 'end' | 'center' | 'baseline' | 'stretch'
  shrink?: CSSProperties['flexShrink']
  styles?: StyleProps
  wrap?: CSSProperties['flexWrap'] | true
}

export type FlexProps = FlexShorthands & {
  children?: ReactNode
}

const flexShorthands = arrayOfAll<keyof FlexShorthands>()([
  'col',
  'columnGap',
  'content',
  'gap',
  'grow',
  'items',
  'justify',
  'justifyItems',
  'justifySelf',
  'reverse',
  'rowGap',
  'self',
  'shrink',
  'styles',
  'wrap',
])

/**
 * Adapted from Tailwind CSS.
 *
 * @see https://tailwindcss.com/docs/align-content
 * @see https://tailwindcss.com/docs/align-items
 * @see https://tailwindcss.com/docs/align-self
 * @see https://tailwindcss.com/docs/justify-content
 * @see https://tailwindcss.com/docs/justify-items
 * @see https://tailwindcss.com/docs/justify-self
 */
export const Flex = styled('div', {
  shouldForwardProp: (prop) => !(flexShorthands as string[]).includes(prop),
})<FlexProps>((props) => {
  const flexDirection = props.col
    ? props.reverse
      ? 'column-reverse'
      : 'column'
    : props.reverse
      ? 'row-reverse'
      : undefined

  return {
    ...(props.content ? { alignContent: FLEX_VALUE_MAP[props.content] } : {}),
    ...(props.items ? { alignItems: FLEX_VALUE_MAP[props.items] } : {}),
    ...(props.self ? { alignSelf: FLEX_VALUE_MAP[props.self] } : {}),
    ...(props.justify ? { justifyContent: FLEX_VALUE_MAP[props.justify] } : {}),
    ...(props.justifyItems
      ? { justifyItems: FLEX_VALUE_MAP[props.justifyItems] }
      : {}),
    ...(props.justifySelf
      ? { justifySelf: FLEX_VALUE_MAP[props.justifySelf] }
      : {}),
    ...(flexDirection ? { flexDirection } : {}),
    ...createStyles({
      ...(props.columnGap ? { columnGap: props.columnGap } : {}),
      ...(props.gap ? { gap: props.gap } : {}),
      ...(props.grow !== undefined ? { flexGrow: props.grow } : {}),
      ...(props.rowGap ? { rowGap: props.rowGap } : {}),
      ...(props.shrink !== undefined ? { flexShrink: props.shrink } : {}),
      ...(props.wrap !== undefined
        ? { flexWrap: props.wrap === true ? 'wrap' : props.wrap }
        : {}),
      ...props.styles,
    }),
    display: 'flex',
  }
})
